import { MapPin, Phone, Mail, Clock, MessageCircle, ChevronRight } from "lucide-react";
import { RESTAURANT, WHATSAPP_URL, WHATSAPP_DISPLAY } from "@/lib/assets";

const rows = [
  { icon: MapPin, label: "Adresse", value: RESTAURANT.address, href: "https://www.google.com/maps/search/?api=1&query=Rue+Chaponni%C3%A8re+7+1201+Gen%C3%A8ve", testid: "contact-address" },
  { icon: Phone, label: "Téléphone", value: RESTAURANT.phone, href: `tel:${RESTAURANT.phone_tel}`, testid: "contact-phone" },
  { icon: Mail, label: "E-mail", value: RESTAURANT.email, href: `mailto:${RESTAURANT.email}`, testid: "contact-email" },
];

const WA_ICON_PATH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z";

export default function ContactPage() {
  return (
    <div data-testid="page-contact" className="pt-24 sm:pt-28">
      {/* ===================== HEADER ===================== */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          data-testid="bg-word"
          className="pointer-events-none select-none absolute inset-x-0 top-14 sm:top-16 font-serif-display italic leading-[0.85] whitespace-nowrap text-center text-[24vw] sm:text-[18vw] text-[#8A1C29]/[0.05]"
        >
          Contact
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-20 pb-10 sm:pb-14">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
          <div className="lg:col-span-8">
            <span className="lm-eyebrow">Contact</span>
            <h1 className="font-serif-display text-[2.25rem] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-5 sm:mt-8 text-[#1A1A18]">
              Réservez une table
              <br />
              <em className="italic text-[#8A1C29]">par WhatsApp</em>.
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[#5C544F] text-[14px] sm:text-[16px] leading-relaxed">
              Le moyen le plus simple de nous joindre. Un message,
              une réponse — souvent dans l&rsquo;heure.
            </p>
          </div>
        </div>
        </div>
      </section>

      {/* ===================== WHATSAPP CARD ===================== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pb-14 sm:pb-20">
        <div className="grid lg:grid-cols-12 gap-4 sm:gap-6">
          <div
            data-testid="whatsapp-card"
            className="lg:col-span-8 relative overflow-hidden bg-[#1A1A18] text-[#FDFBF7] p-7 sm:p-10 lg:p-14"
          >
            <div
              aria-hidden
              className="absolute -top-16 -right-16 sm:-top-20 sm:-right-20 h-64 w-64 sm:h-80 sm:w-80 rounded-full bg-[#25D366]/15 blur-3xl"
            />
            <div className="relative">
              <div className="flex items-center gap-3 text-[#25D366]">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#25D366]">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-white">
                    <path d={WA_ICON_PATH} />
                  </svg>
                </span>
                <span className="text-[11px] uppercase tracking-[0.28em] font-medium">
                  WhatsApp · réponse rapide
                </span>
              </div>
              <h2 className="font-serif-display text-2xl sm:text-4xl lg:text-5xl leading-tight mt-6 sm:mt-8 max-w-xl">
                Écrivez-nous, on s&rsquo;occupe du reste.
              </h2>
              <p className="mt-5 sm:mt-6 max-w-lg text-[14px] sm:text-[16px] leading-relaxed text-white/75">
                Pour une réservation, indiquez simplement le <strong className="text-white font-medium">jour</strong>,
                l&rsquo;<strong className="text-white font-medium">heure</strong> et le
                <strong className="text-white font-medium"> nombre de personnes</strong>.
                On vous confirme dans la foulée.
              </p>

              <div className="mt-7 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="contact-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-[#25D366] hover:bg-[#128C7E] text-white uppercase tracking-[0.14em] text-[12px] font-semibold transition-colors rounded-full shadow-[0_10px_30px_-8px_rgba(37,211,102,0.55)]"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d={WA_ICON_PATH} />
                  </svg>
                  Ouvrir WhatsApp
                  <ChevronRight size={14} />
                </a>
                <span className="text-[13px] text-white/60 tracking-wide">
                  ou composez le <a href="tel:+41764254935" className="text-white hover:text-[#25D366]">{WHATSAPP_DISPLAY}</a>
                </span>
              </div>

              <div className="mt-10 pt-7 border-t border-white/10 grid grid-cols-3 gap-4 text-[12px] sm:text-[13px]">
                <Step n="1" t="Écrivez" body="Jour, heure, nombre de personnes." />
                <Step n="2" t="Confirmation" body="On vous répond en direct." />
                <Step n="3" t="À table" body="Rue Chaponnière 7." />
              </div>
            </div>
          </div>

          {/* Side info */}
          <div className="lg:col-span-4 space-y-3 sm:space-y-4">
            {rows.map((r) => {
              const Icon = r.icon;
              return (
                <a
                  key={r.label}
                  href={r.href}
                  target={r.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer noopener"
                  data-testid={r.testid}
                  className="group flex items-start gap-4 border border-[#E8E2D9] bg-[#FDFBF7] p-5 hover:border-[#8A1C29]/40 transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-[#8A1C29]/30 text-[#8A1C29] group-hover:bg-[#8A1C29] group-hover:text-[#FDFBF7] transition-colors shrink-0">
                    <Icon size={16} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#5C544F]">{r.label}</div>
                    <div className="font-serif-display text-[16px] sm:text-[17px] text-[#1A1A18] mt-1 truncate">{r.value}</div>
                  </div>
                </a>
              );
            })}
            <div className="flex items-start gap-4 border border-[#E8E2D9] bg-[#F3EFE9] p-5">
              <div className="w-10 h-10 flex items-center justify-center border border-[#8A1C29]/30 text-[#8A1C29] shrink-0">
                <Clock size={16} strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#5C544F]">Horaires</div>
                <div className="font-serif-display text-[16px] sm:text-[17px] text-[#1A1A18] mt-1 leading-tight">
                  Lun – Sam · 10h – 22h
                </div>
                <div className="text-[12px] text-[#5C544F] mt-1.5">Fermé le dimanche</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== MAP ===================== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pb-20 sm:pb-28">
        <div className="flex items-baseline justify-between gap-4 mb-5 sm:mb-6">
          <div>
            <span className="lm-eyebrow">Nous trouver</span>
            <h2 className="font-serif-display text-[1.5rem] sm:text-3xl lg:text-4xl mt-3 sm:mt-4 text-[#1A1A18]">
              Rue Chaponnière 7 · 1201 Genève
            </h2>
          </div>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Rue+Chaponni%C3%A8re+7+1201+Gen%C3%A8ve"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="contact-cta-directions"
            className="text-[#8A1C29] uppercase tracking-[0.22em] text-[11px] sm:text-[12px] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all shrink-0"
          >
            Itinéraire <ChevronRight size={14} />
          </a>
        </div>
        <div className="min-h-[340px] sm:min-h-[440px]">
          <iframe
            title="Carte La Matze"
            data-testid="contact-map"
            src="https://www.google.com/maps?q=Rue+Chaponni%C3%A8re+7,+1201+Gen%C3%A8ve&output=embed"
            className="w-full h-full min-h-[340px] sm:min-h-[440px] border border-[#E8E2D9]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}

function Step({ n, t, body }) {
  return (
    <div className="flex flex-col">
      <span className="font-serif-display italic text-[#25D366]/80 text-lg leading-none mb-1.5">
        {n}
      </span>
      <span className="text-white font-medium">{t}</span>
      <span className="text-white/60 mt-0.5">{body}</span>
    </div>
  );
}
